import { CatalogsQuery } from 'src/ts/@types/generated/graphql/catalog-service/graphql';
import { DisplayFilters } from './display-filters';

export const applyFilters = (input: {
  catalogs: CatalogsQuery['catalogs'];
  filters: DisplayFilters;
}): CatalogsQuery['catalogs'] => {
  const { catalogs, filters } = input;

  const filteredCatalogs = catalogs.map((catalog) => {
    return {
      ...catalog,
      items: catalog.items.filter((catalogItem) => {
        const eligibilityInput = {
          catalogItem,
          filters,
        };

        return [
          isTagEligible(eligibilityInput),
          isIngredientsToAvoidEligible(eligibilityInput),
          isMacroEligible(eligibilityInput),
          isChefEligible(eligibilityInput),
        ].every((filterResult) => filterResult === true);
      }),
    };
  });

  return filteredCatalogs;
};

const isTagEligible = (input: {
  catalogItem: CatalogsQuery['catalogs'][0]['items'][0];
  filters: DisplayFilters;
}): boolean => {
  const { catalogItem, filters } = input;

  // If there are no applied TagCategory filter, this item is automatically eligible
  if (!filters.tagCategories || filters.tagCategories.length === 0) {
    return true;
  }

  // If there are no tags available on this item it must be ineligible
  const itemTags = catalogItem?.variant.tags;
  if (!itemTags || itemTags.length === 0) {
    return false;
  }

  // @TODO AllMealHack: If the only thing you're filtering by is the Tag All Meals
  if (
    filters.tagCategories.length === 1 &&
    filters.tagCategories[0].tags.length === 1 &&
    filters.tagCategories[0].tags[0].logicalId === 'Personalized'
  ) {
    return true;
  }

  for (const filterTagCategory of filters.tagCategories) {
    // @TODO AllMealHack: If the Personzlied filter exists, skip it. This tag never
    // appears on items and is always valid.
    if (filterTagCategory.logicalId === 'DIETARY_TEMPLATE') {
      if (
        filterTagCategory.tags.length === 1 &&
        filterTagCategory.tags[0].logicalId === 'Personalized'
      ) {
        continue;
      }
    }

    if (filterTagCategory.tags.length === 0) {
      continue;
    }

    // For this category, does this catalogItem contain any of the filter flags?
    const itemHasTagInCategory = itemTags.some(
      (itemTag) =>
        itemTag &&
        filterTagCategory.tags.map((tag) => tag.id).includes(itemTag.id),
    );

    if (!itemHasTagInCategory) {
      return false;
    }
  }

  return true;
};

const isIngredientsToAvoidEligible = (input: {
  catalogItem: CatalogsQuery['catalogs'][0]['items'][0];
  filters: DisplayFilters;
}): boolean => {
  const {
    catalogItem,
    filters: { ingredientsToAvoid },
  } = input;

  // This filter only works against MealVariants
  if (catalogItem?.variant.__typename !== 'MealVariant') {
    return true;
  }

  const variantIngredientToAvoidIds =
    catalogItem.variant.ingredientsToAvoid.map(
      (variantIngredientToAvoid) =>
        variantIngredientToAvoid.legacyAllergenItemId,
    );

  const containsIngredientsToAvoidOverlap = ingredientsToAvoid?.find(
    (ingredientToAvoid) =>
      variantIngredientToAvoidIds.includes(
        ingredientToAvoid.legacyAllergenItemId,
      ),
  );

  if (containsIngredientsToAvoidOverlap) {
    return false;
  }

  return true;
};

const isMacroEligible = (input: {
  catalogItem: CatalogsQuery['catalogs'][0]['items'][0];
  filters: DisplayFilters;
}): boolean => {
  const {
    catalogItem,
    filters: { macros },
  } = input;

  // Can only filter macros on meal variants
  if (catalogItem?.variant.__typename !== 'MealVariant') {
    return true;
  }

  // If no filter macros provided, nothing to filter
  if (!macros) {
    return true;
  }

  const itemMacros = catalogItem.variant.macroNutrients;

  for (const macroFilter of macros) {
    const itemMacroValue = itemMacros[
      macroFilter.key as keyof typeof itemMacros
    ] as number;

    // If there's a max defined and you're above it, invalid
    if (macroFilter.max !== null && itemMacroValue > macroFilter.max) {
      return false;
    }

    // If there's a minimum defined and you're below it, invalid
    if (macroFilter.min > itemMacroValue) {
      return false;
    }
  }

  return true;
};

const isChefEligible = (input: {
  catalogItem: CatalogsQuery['catalogs'][0]['items'][0];
  filters: DisplayFilters;
}): boolean => {
  const {
    catalogItem,
    filters: { chefNames },
  } = input;

  // Can only filter chefs on meal variants
  if (catalogItem?.variant.__typename !== 'MealVariant') {
    return true;
  }

  // If no filter provided, nothing to filter
  if (!chefNames || chefNames.length === 0) {
    return true;
  }

  return chefNames.includes(catalogItem.variant.product.designer.name);
};
